import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  UserPlus, Trash2, Shield, ShieldOff, Crown, Pencil,
  Users, Building2, Loader2, CheckSquare, X as XIcon, LogIn,
} from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERMISSION_MODULES = [
  { key: 'tasks',           label: 'Tasks' },
  { key: 'production',      label: 'Production' },
  { key: 'maintenance',     label: 'Maintenance' },
  { key: 'diesel',          label: 'Diesel' },
  { key: 'inventory',       label: 'Inventory / Store' },
  { key: 'parts_release',   label: 'Parts Release' },
  { key: 'purchase_orders', label: 'Purchase Orders' },
  { key: 'analytics',       label: 'Reports & Analytics' },
  { key: 'assets',          label: 'Assets' },
  { key: 'scrap',           label: 'Scrap' },
  { key: 'tyres',           label: 'Tyres' },
  { key: 'activity_log',    label: 'Activity Log' },
];

const ACTIONS = [
  { key: 'view',              label: 'View' },
  { key: 'edit',              label: 'Edit / Add' },
  { key: 'approve_deletions', label: 'Approve Deletions' },
];

// ── Permission Override Panel ─────────────────────────────────────────────────
const PermissionOverridesPanel = ({ effectivePermissions, overrides, onChange }) => {
  const hasAnyOverride = Object.keys(overrides).length > 0;

  const getEffective = (module, action) => {
    if (overrides[module] && overrides[module][action] !== undefined)
      return overrides[module][action];
    return effectivePermissions?.[module]?.[action] ?? false;
  };

  const isOverridden = (module, action) =>
    overrides[module] && overrides[module][action] !== undefined;

  const toggle = (module, action) => {
    const current = getEffective(module, action);
    const newOverrides = { ...overrides };
    if (!newOverrides[module]) newOverrides[module] = {};
    newOverrides[module] = { ...newOverrides[module], [action]: !current };
    onChange(newOverrides);
  };

  const clearOverride = (module, action) => {
    const newOverrides = { ...overrides };
    if (!newOverrides[module]) return;
    const { [action]: _, ...rest } = newOverrides[module];
    if (Object.keys(rest).length === 0) delete newOverrides[module];
    else newOverrides[module] = rest;
    onChange(newOverrides);
  };

  const resetAll = () => onChange({});

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-900">Permission Overrides</p>
          <p className="text-xs text-slate-500">Toggles below override the assigned role. Amber = overridden.</p>
        </div>
        {hasAnyOverride && (
          <button type="button" onClick={resetAll}
            className="text-xs text-red-500 hover:text-red-700 underline">
            Reset all overrides
          </button>
        )}
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Module</th>
              {ACTIONS.map(a => (
                <th key={a.key} className="text-center px-2 py-2 font-medium text-slate-600 w-24">{a.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {PERMISSION_MODULES.map(mod => (
              <tr key={mod.key} className="hover:bg-slate-50">
                <td className="px-3 py-1.5 text-slate-700 font-medium">{mod.label}</td>
                {ACTIONS.map(action => {
                  const val = getEffective(mod.key, action.key);
                  const overridden = isOverridden(mod.key, action.key);
                  return (
                    <td key={action.key} className="text-center px-2 py-1.5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => overridden ? clearOverride(mod.key, action.key) : toggle(mod.key, action.key)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                            val
                              ? overridden ? 'bg-amber-500' : 'bg-emerald-500'
                              : overridden ? 'bg-amber-200' : 'bg-slate-200'
                          }`}
                          data-testid={`perm-${mod.key}-${action.key}`}
                          title={overridden ? 'Click to reset to role default' : 'Click to override'}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${val ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                        {overridden && (
                          <span className="text-amber-600 font-bold text-xs" title="Overridden">*</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasAnyOverride && (
        <p className="text-xs text-amber-600">
          * Amber toggles are overrides. Click a toggle to flip it; click again (when amber) to restore role default.
        </p>
      )}
    </div>
  );
};

const emptyForm = () => ({
  full_name: '', email: '', password: '',
  department: '', role_id: '', is_admin: false,
  permission_overrides: {},
});

// ── Main Component ────────────────────────────────────────────────────────────
const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRoleDialogOpen, setBulkRoleDialogOpen] = useState(false);
  const [bulkRoleId, setBulkRoleId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: authHeaders() }),
        axios.get(`${API}/roles`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setAvailableRoles(rolesRes.data);
    } catch {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getRoleById = (roleId) => availableRoles.find(r => r.id === roleId);

  // ── Bulk selection helpers ─────────────────────────────────────────────────
  // Selectable = non-super-admins that are not the current user
  const selectableUsers = users.filter(u => !u.is_super_admin && u.id !== user?.id);
  const allSelected = selectableUsers.length > 0 && selectableUsers.every(u => selectedIds.has(u.id));
  const someSelected = selectableUsers.some(u => selectedIds.has(u.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableUsers.map(u => u.id)));
    }
  };

  const toggleSelectUser = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Bulk role assign ───────────────────────────────────────────────────────
  const handleBulkAssignRole = async () => {
    if (!bulkRoleId && bulkRoleId !== '__clear__') return;
    setBulkSaving(true);
    const roleIdToSet = bulkRoleId === '__clear__' ? null : bulkRoleId;
    try {
      await Promise.all(
        [...selectedIds].map(uid =>
          axios.put(`${API}/admin/users/${uid}`, { role_id: roleIdToSet }, { headers: authHeaders() })
        )
      );
      const label = roleIdToSet
        ? getRoleById(roleIdToSet)?.name || 'selected role'
        : 'no role';
      toast.success(`Assigned "${label}" to ${selectedIds.size} user(s)`);
      setSelectedIds(new Set());
      setBulkRoleDialogOpen(false);
      setBulkRoleId('');
      fetchData();
    } catch {
      toast.error('Some updates failed — please try again');
    } finally {
      setBulkSaving(false);
    }
  };

  // ── Create user ────────────────────────────────────────────────────────────
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users`, {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        department: formData.department || '',
        role_id: formData.role_id || null,
        is_admin: formData.is_admin,
        role: 'custom',
      }, { headers: authHeaders() });
      toast.success('User created successfully');
      setIsCreateOpen(false);
      setFormData(emptyForm());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  // ── Update user ────────────────────────────────────────────────────────────
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        full_name: formData.full_name,
        department: formData.department || '',
        role_id: formData.role_id || null,
        is_admin: formData.is_admin,
        permission_overrides: formData.permission_overrides,
      }, { headers: authHeaders() });
      toast.success('User updated');
      setEditingUser(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setFormData({
      full_name: u.full_name,
      email: u.email,
      password: '',
      department: u.department || '',
      role_id: u.role_id || '',
      is_admin: u.is_admin,
      permission_overrides: u.permission_overrides || {},
    });
  };

  // ── Delete user ───────────────────────────────────────────────────────────
  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, { headers: authHeaders() });
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  // ── Toggle admin ──────────────────────────────────────────────────────────
  const toggleAdmin = async (targetUser) => {
    try {
      await axios.put(`${API}/admin/users/${targetUser.id}`, {
        full_name: targetUser.full_name,
        is_admin: !targetUser.is_admin,
      }, { headers: authHeaders() });
      toast.success(`Admin ${!targetUser.is_admin ? 'granted' : 'revoked'}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // ── Impersonate user (Super Admin only) ───────────────────────────────────
  const handleImpersonate = async (targetUser) => {
    try {
      const res = await axios.post(
        `${API}/admin/impersonate/${targetUser.id}`,
        {},
        { headers: authHeaders() }
      );
      // Store original token and switch to the impersonated one
      localStorage.setItem('original_token', localStorage.getItem('token'));
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('impersonating', JSON.stringify({
        name: targetUser.full_name,
        email: targetUser.email,
      }));
      window.location.href = '/';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const isDeptHeadView = !user?.is_admin && !user?.is_super_admin && user?.permissions?.can_manage_users;

  if (loading) return (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6" />
            {isDeptHeadView ? 'My Team' : 'User Management'}
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {isDeptHeadView
              ? `Showing members of the ${user.department || 'your'} department`
              : 'Manage users and assign roles to control access'}
          </p>
        </div>
        <Button
          onClick={() => { setFormData(emptyForm()); setIsCreateOpen(true); }}
          data-testid="add-user-btn"
          className="bg-slate-900 hover:bg-slate-800"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Bulk Action Bar — appears when rows are selected */}
      {someSelected && (
        <div
          className="flex items-center justify-between bg-slate-900 text-white px-4 py-2.5 rounded-lg shadow-lg"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium">
            {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="bg-white text-slate-900 hover:bg-slate-100 h-8 text-xs"
              onClick={() => { setBulkRoleId(''); setBulkRoleDialogOpen(true); }}
              data-testid="bulk-assign-role-btn"
            >
              <CheckSquare className="h-3.5 w-3.5 mr-1.5" />
              Assign Role
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 text-xs"
              onClick={() => setSelectedIds(new Set())}
              data-testid="clear-selection-btn"
            >
              <XIcon className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                  data-testid="select-all-checkbox"
                  className={someSelected && !allSelected ? 'opacity-50' : ''}
                />
              </TableHead>
              <TableHead className="w-64">User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Department</TableHead>
              <TableHead className="text-center">Tasks</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const roleDoc = getRoleById(u.role_id);
              const isSelectable = !u.is_super_admin && u.id !== user?.id;
              return (
                <TableRow
                  key={u.id}
                  className={selectedIds.has(u.id) ? 'bg-slate-50' : ''}
                >
                  <TableCell>
                    {isSelectable ? (
                      <Checkbox
                        checked={selectedIds.has(u.id)}
                        onCheckedChange={() => toggleSelectUser(u.id)}
                        aria-label={`Select ${u.full_name}`}
                        data-testid={`checkbox-${u.id}`}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{u.full_name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.is_super_admin ? (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">
                        <Crown className="h-3 w-3 mr-1" />Super Admin
                      </Badge>
                    ) : u.is_admin && !u.role_id ? (
                      <Badge className="bg-purple-100 text-purple-800 text-xs">
                        <Shield className="h-3 w-3 mr-1" />Admin
                      </Badge>
                    ) : roleDoc ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${roleDoc.color}20`,
                          color: roleDoc.color,
                          border: `1px solid ${roleDoc.color}40`,
                        }}
                        data-testid={`role-badge-${u.id}`}
                      >
                        {roleDoc.name}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">No role assigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.department ? (
                      <span className="text-xs flex items-center gap-1 text-slate-600">
                        <Building2 className="h-3 w-3" />{u.department}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-sm">{u.tasks_created || 0}</TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {/* Edit button */}
                      {!u.is_super_admin && u.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(u)}
                          title="Edit user"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                      )}
                      {/* Login as — Super Admin only */}
                      {user?.is_super_admin && !u.is_super_admin && u.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleImpersonate(u)}
                          title={`Login as ${u.full_name}`}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          data-testid={`impersonate-${u.id}`}
                        >
                          <LogIn className="h-3.5 w-3.5 mr-1" />
                          Login as
                        </Button>
                      )}
                      {/* Admin + Delete */}
                      {u.id !== user?.id && !u.is_super_admin && (user?.is_super_admin || user?.is_admin) && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleAdmin(u)}
                            title={u.is_admin ? 'Revoke Admin' : 'Make Admin'}
                            data-testid={`toggle-admin-${u.id}`}
                          >
                            {u.is_admin
                              ? <ShieldOff className="h-3.5 w-3.5 text-orange-500" />
                              : <Shield className="h-3.5 w-3.5 text-blue-500" />
                            }
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* ── Create User Modal ─────────────────────────────────────────────── */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label>Full Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.full_name}
                onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                required className="mt-1.5"
                data-testid="user-fullname-input"
              />
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                required className="mt-1.5"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>Password <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                required className="mt-1.5"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>Department</Label>
              <Input
                placeholder="e.g. Production, Diesel, Maintenance"
                value={formData.department}
                onChange={e => setFormData({ ...formData, department: e.target.value })}
                className="mt-1.5"
                data-testid="user-department-input"
              />
            </div>
            <div>
              <Label>Assign Role</Label>
              <Select
                value={formData.role_id || '__none__'}
                onValueChange={v => setFormData({ ...formData, role_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No role assigned</SelectItem>
                  {availableRoles.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                        {r.name}
                        {r.department && <span className="text-slate-400 text-xs">({r.department})</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.role_id && getRoleById(formData.role_id)?.description && (
                <p className="text-xs text-slate-500 mt-1">{getRoleById(formData.role_id).description}</p>
              )}
            </div>
            {(user?.is_admin || user?.is_super_admin) && (
              <div className="flex items-center justify-between py-1">
                <div>
                  <Label>Admin Access</Label>
                  <p className="text-xs text-slate-500">Allows user to manage other users</p>
                </div>
                <Switch
                  checked={formData.is_admin}
                  onCheckedChange={v => setFormData({ ...formData, is_admin: v })}
                  data-testid="user-admin-switch"
                />
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="create-user-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit User Modal ──────────────────────────────────────────────── */}
      <Dialog open={!!editingUser} onOpenChange={open => { if (!open) setEditingUser(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User — {editingUser?.full_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-5">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1.5"
                  data-testid="edit-fullname-input"
                />
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  placeholder="e.g. Production, Diesel"
                  value={formData.department}
                  onChange={e => setFormData({ ...formData, department: e.target.value })}
                  className="mt-1.5"
                  data-testid="edit-department-input"
                />
              </div>
            </div>
            <div>
              <Label>Assign Role</Label>
              <Select
                value={formData.role_id || '__none__'}
                onValueChange={v => setFormData({ ...formData, role_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="edit-role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No role assigned</SelectItem>
                  {availableRoles.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                        {r.name}
                        {r.department && <span className="text-slate-400 text-xs">({r.department})</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.role_id && getRoleById(formData.role_id)?.description && (
                <p className="text-xs text-slate-500 mt-1">{getRoleById(formData.role_id).description}</p>
              )}
            </div>
            {(user?.is_admin || user?.is_super_admin) && (
              <div className="flex items-center justify-between py-1">
                <div>
                  <Label>Admin Access</Label>
                  <p className="text-xs text-slate-500">Allows user to manage other users</p>
                </div>
                <Switch
                  checked={formData.is_admin}
                  onCheckedChange={v => setFormData({ ...formData, is_admin: v })}
                  data-testid="edit-admin-switch"
                />
              </div>
            )}

            {/* Permission Overrides */}
            {!editingUser?.is_super_admin && (
              <div className="border-t pt-4">
                <PermissionOverridesPanel
                  effectivePermissions={editingUser?.permissions}
                  overrides={formData.permission_overrides || {}}
                  onChange={v => setFormData({ ...formData, permission_overrides: v })}
                />
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="update-user-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Assign Role Dialog ───────────────────────────────────────── */}
      <Dialog open={bulkRoleDialogOpen} onOpenChange={open => { if (!open) setBulkRoleDialogOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-slate-700" />
              Assign Role to {selectedIds.size} User{selectedIds.size !== 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-500">
              Select a role to apply to all selected users at once.
            </p>
            <div>
              <Label>Role</Label>
              <Select
                value={bulkRoleId || '__none__'}
                onValueChange={v => setBulkRoleId(v)}
              >
                <SelectTrigger className="mt-1.5" data-testid="bulk-role-select">
                  <SelectValue placeholder="Choose a role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>Choose a role...</SelectItem>
                  <SelectItem value="__clear__">
                    <span className="text-slate-400 italic">Clear role (remove assignment)</span>
                  </SelectItem>
                  {availableRoles.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                        {r.name}
                        {r.department && <span className="text-slate-400 text-xs">({r.department})</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkRoleDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkAssignRole}
              disabled={bulkSaving || !bulkRoleId || bulkRoleId === '__none__'}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="bulk-role-confirm-btn"
            >
              {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Apply to {selectedIds.size} User{selectedIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm ────────────────────────────────────────────────── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.full_name}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
